import { Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import MiddleFormCard from "../../components/cards/MiddleFormCard";
import { PasswordInput, TextInput } from "../../components/inputs";
import SubmitButton from "../../components/SubmitButton";
import { useAuth } from "../../contexts/AuthContext";
import { useLogin } from "../../contexts/LoginContext";
import { LoginFormData, loginSchema } from "./types";
import { useApp } from "../../contexts/AppContext";
import { useApi } from "../../contexts/ApiContext";
import { getCurrentUser } from "aws-amplify/auth";

const Login: React.FC = () => {
  const [formErrors, setFormErrors] = useState<any>([]);
  const [validateOnChange, setValidateOnChange] = useState(false);

  const { email, password, setEmail, setPassword } = useLogin();
  const { signIn, checkUser } = useAuth();

  const { translate } = useApp();
  const { setSubmitting } = useApi();
  const authTranslations = useMemo(() => translate("auth"), [translate]);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (validateOnChange) {
      validate();
    }
  }, [email, password]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const u = queryParams.get("u");
    const p = queryParams.get("p");
    if (u && p) {
      setEmail(u);
      setPassword(p);
    }
  }, [location.search]);

  const handleSubmit = async () => {
    setValidateOnChange(true);
    if (!validate()) {
      return;
    }
    try {
      setSubmitting(true);
      await signIn(email, password);

      setFormErrors({});
      console.info("login: current user", await getCurrentUser());
      window.location.href = "/dashboard";
    } catch (error: any) {
      debugger;

      console.log("login error", error);
      if (error?.name === "UserNeedsNewPassword") {
        navigate("/signup/newpassword", {
          state: { username: error.username },
        });
        return;
      }
      if (error?.name === "UserAlreadyAuthenticatedException") {
        window.location.href = "/";
      }

      setFormErrors({ password: authTranslations.errors.invalid_login });
    } finally {
      setSubmitting(false);
    }
  };

  const validate = () => {
    let formData: LoginFormData = { email, password };
    if (email.search("@") === -1) {
      formData = { username: email, password };
    } else {
      formData = { email, password };
    }

    const result = loginSchema.safeParse(formData);
    if (!result.success) {
      const errorDetails = result.error.flatten().fieldErrors;
      console.log("errorDetails", errorDetails);
      setFormErrors(errorDetails as Partial<LoginFormData>);
      setValidateOnChange(true);
      return false;
    } else {
      setFormErrors({});
    }
    return true;
  };

  return (
    <MiddleFormCard
      onSubmit={handleSubmit}
      title={authTranslations.login.welcome}
      subtitle={authTranslations.login.subtitle}
    >
      <React.Fragment>
        <TextInput
          label={authTranslations.login.fields.useremail}
          value={email}
          onChange={(value: number | string) => setEmail(value as string)}
          error={formErrors.username}
          helperText={translate("auth", formErrors.username?.[0])}
        />
        <PasswordInput
          label={authTranslations.login.fields.password}
          value={password}
          onChange={(value: string) => setPassword(value)}
          error={formErrors.password}
          helperText={translate("auth", formErrors.password?.[0])}
          topRight={
            <Link to={"/login/forgot"}>
              {authTranslations.login.buttons.forgot}
            </Link>
          }
        />
        <SubmitButton onClick={handleSubmit} color="blackButton" fullWidth>
          {authTranslations.login.buttons.submit}
        </SubmitButton>
        <Typography
          textAlign={"center"}
          variant="body2"
          color="textSecondary"
          mt={2}
        >
          {authTranslations.login.dont_have_account}{" "}
          <Link to={"/signup"} style={{ fontWeight: "bold" }}>
            {authTranslations.login.buttons.signup}
          </Link>
        </Typography>
      </React.Fragment>
    </MiddleFormCard>
  );
};
export default Login;
